import { generateUUID } from 'services/base/uuid';
import { CustomPolicy, Schedule, SchedulePolicy } from 'services/pangolin/types';
import {
  SchedulePolicyCategory,
  SchedulePolicyCategoryMap,
  SchedulePolicyKind,
  SchedulePolicyKindMap,
} from 'services/pangolin/protos/schedules_pb';
import { UnreachableFunction } from 'ui/base/types';
import Messages from './policy_entry_messages';

export type EVERYTHING_POLICY = 9999;
export const EVERYTHING_POLICY: EVERYTHING_POLICY = 9999;
export type EVERYTHING_POLICY_ID = 'EVERYTHING_POLICY';
export const EVERYTHING_POLICY_ID: EVERYTHING_POLICY_ID = 'EVERYTHING_POLICY';
export type EverythingPolicy = {
  kind: EVERYTHING_POLICY;
  uuid: EVERYTHING_POLICY_ID;
};

type KnownCategory =
  | SchedulePolicyCategoryMap['C_ADULT']
  | SchedulePolicyCategoryMap['C_ENTERTAINMENT']
  | SchedulePolicyCategoryMap['C_GAMES']
  | SchedulePolicyCategoryMap['C_SOCIAL'];

export type KnownCategoryPolicy = {
  kind: SchedulePolicyKindMap['P_CATEGORY'];
  uuid: string;
  category: KnownCategory;
};

export type PolicyEntry =
  | EverythingPolicy
  | KnownCategoryPolicy
  | CustomPolicy;

export type PolicyIconStyles = {
  entireInternetIcon: string;
  adultSitesIcon: string;
  entertainmentSitesIcon: string;
  gamingSitesIcon: string;
  socialMediaIcon: string;
};

export const getPolicyEntryIcon = (styles: PolicyIconStyles, policy: PolicyEntry): string | undefined => {
  switch(policy.kind) {
    case EVERYTHING_POLICY:
      return styles.entireInternetIcon;
    case SchedulePolicyKind.P_CUSTOM:
      return undefined;
    case SchedulePolicyKind.P_CATEGORY:
      switch(policy.category) {
        case SchedulePolicyCategory.C_ADULT:
          return styles.adultSitesIcon;
        case SchedulePolicyCategory.C_ENTERTAINMENT:
          return styles.entertainmentSitesIcon;
        case SchedulePolicyCategory.C_GAMES:
          return styles.gamingSitesIcon;
        case SchedulePolicyCategory.C_SOCIAL:
          return styles.socialMediaIcon;
        default:
          return UnreachableFunction(policy.category);
      }
    default:
      return UnreachableFunction(policy);
  }
};

export const getPolicyEntryLabel = (policy: PolicyEntry): string => {
  switch(policy.kind) {
    case EVERYTHING_POLICY:
      return Messages.entireInternet();
    case SchedulePolicyKind.P_CUSTOM:
      return policy.custom;
    case SchedulePolicyKind.P_CATEGORY:
      switch(policy.category) {
        case SchedulePolicyCategory.C_ADULT:
          return Messages.adultSites();
        case SchedulePolicyCategory.C_ENTERTAINMENT:
          return Messages.entertainmentSites();
        case SchedulePolicyCategory.C_GAMES:
          return Messages.gamingSites();
        case SchedulePolicyCategory.C_SOCIAL:
          return Messages.socialMedia();
        default:
          return UnreachableFunction(policy.category);
      }
    default:
      return UnreachableFunction(policy);
  }
};

export const getPolicyEntryID = (entry: PolicyEntry): string => {
  return entry.uuid;
};

export function isKnownCategoryPolicy(x: SchedulePolicy): x is KnownCategoryPolicy {
  if (x.kind !== SchedulePolicyKind.P_CATEGORY) {
    return false;
  } else if (x.category === SchedulePolicyCategory.C_UNKNOWN) {
    return false;
  }
  return true;
}

export function removeEverythingPolicy(entry: PolicyEntry): KnownCategoryPolicy | CustomPolicy | undefined {
  if (entry.kind === SchedulePolicyKind.P_CUSTOM) {
    return entry;
  } else if (entry.kind === SchedulePolicyKind.P_CATEGORY && isKnownCategoryPolicy(entry)) {
    return entry;
  } else {
    return undefined;
  }
}

const CATEGORY_POLICIES = [
  { kind: SchedulePolicyKind.P_CATEGORY, category: SchedulePolicyCategory.C_ADULT },
  { kind: SchedulePolicyKind.P_CATEGORY, category: SchedulePolicyCategory.C_ENTERTAINMENT },
  { kind: SchedulePolicyKind.P_CATEGORY, category: SchedulePolicyCategory.C_GAMES },
  { kind: SchedulePolicyKind.P_CATEGORY, category: SchedulePolicyCategory.C_SOCIAL },
];

export function createPolicyEntries(schedule: Schedule): { entries: PolicyEntry[]; preserved: SchedulePolicy[] } {
  const entries: PolicyEntry[] = [];
  const preserved: SchedulePolicy[] = [];

  // Push in the everything policy if it's there
  if (schedule.applyAllPolicies) {
    entries.push({
      kind: EVERYTHING_POLICY,
      uuid: 'EVERYTHING_POLICY',
    });
  }

  // Push in only the custom and known category policies
  schedule.policies.forEach((policy) => {
    if (policy.kind === SchedulePolicyKind.P_CUSTOM || isKnownCategoryPolicy(policy)) {
      entries.push(policy);
    } else {
      preserved.push(policy);
    }
  });
  return { entries, preserved };
}

export const CUSTOM_DOMAIN_SENTINEL = Messages.customDomain();
export const CUSTOM_IP_SENTINEL = Messages.customIP();

export function createPolicyEntryOptions(existingEntries: PolicyEntry[]): PolicyEntry[] {
  const result: PolicyEntry[] = [];
  const everything = existingEntries.find((e) => e.kind === EVERYTHING_POLICY);
  if (everything != null) {
    result.push(everything);
  } else {
    result.push({
      kind: EVERYTHING_POLICY,
      uuid: EVERYTHING_POLICY_ID,
    });
  }

  const categories: PolicyEntry[] = CATEGORY_POLICIES.map((category) => {
    const existing = existingEntries.find(entry =>
      entry.kind === category.kind && entry.category === category.category);
    if (existing != null) {
      return existing;
    } else {
      return {
        uuid: generateUUID(),
        ...category,
      };
    }
  });
  result.push(...categories);

  result.push({
    kind: SchedulePolicyKind.P_CUSTOM,
    uuid: generateUUID(),
    custom: CUSTOM_DOMAIN_SENTINEL,
  });

  result.push({
    kind: SchedulePolicyKind.P_CUSTOM,
    uuid: generateUUID(),
    custom: CUSTOM_IP_SENTINEL,
  });

  return result;
}

export function createCustomPolicy(custom: string): CustomPolicy {
  return {
    kind: SchedulePolicyKind.P_CUSTOM,
    uuid: generateUUID(),
    custom,
  };
}
